import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { sendAnnouncementDismiss } from '../../api/ws'
import { Markdown } from '../permission/Markdown'
import './AnnouncementOverlay.css'

// Race-debounce window: if a push event arrived this recently, hold the
// overlay so the permission_request frame / push.tapped navigation has
// time to land. Picked to be invisible on success, long enough to lose
// the race against any remotely plausible WS frame.
const PUSH_DEBOUNCE_MS = 500

/// Server-driven CTA. Renders the head of the announcements queue when
/// the suppression gate allows. Gate rules:
///   - empty queue → nothing
///   - permission overlay mounted (any pendingRequests) → suppress
///   - top of navStack is a push-sourced agent_detail → suppress
///   - within PUSH_DEBOUNCE_MS of a push event → suppress (re-evaluated
///     by a one-shot timer)
///
/// Dismissal sends `announcement_dismiss` over the WS and pops the local
/// queue. Server flips status to 'dismissed' so the announcement doesn't
/// re-deliver on next reconnect.
export function AnnouncementOverlay() {
  const announcement = useAppStore((s) => s.announcements[0])
  const pendingCount = useAppStore((s) => s.pendingRequests.length)
  const top = useAppStore((s) => s.navStack[s.navStack.length - 1])
  const lastPushEventAt = useAppStore((s) => s.lastPushEventAt)
  const removeAnnouncement = useAppStore((s) => s.removeAnnouncement)

  const pushOnTop = top?.kind === 'agent_detail' && top.source === 'push'
  const sinceLastPush = lastPushEventAt ? Date.now() - lastPushEventAt : Infinity
  const inPushDebounce = sinceLastPush < PUSH_DEBOUNCE_MS

  // Force re-render when the debounce window expires so the overlay
  // can flip from suppressed → visible without waiting for some other
  // store change to trigger React.
  const [, tick] = useState(0)
  useEffect(() => {
    if (!announcement) return
    if (!inPushDebounce) return
    const t = setTimeout(
      () => tick((n) => n + 1),
      PUSH_DEBOUNCE_MS - sinceLastPush + 10,
    )
    return () => clearTimeout(t)
  }, [announcement, inPushDebounce, sinceLastPush])

  if (!announcement) return null
  if (pendingCount > 0) return null
  if (pushOnTop) return null
  if (inPushDebounce) return null

  function dismiss() {
    if (!announcement) return
    sendAnnouncementDismiss(announcement.id)
    removeAnnouncement(announcement.id)
  }

  return (
    <div className="announcement-overlay" role="dialog" aria-modal="true">
      <div className="announcement-overlay__card">
        <h2 className="announcement-overlay__title">{announcement.title}</h2>
        <div className="announcement-overlay__body">
          <Markdown source={announcement.body} />
        </div>
        <button
          type="button"
          className="announcement-overlay__dismiss"
          onClick={dismiss}
        >
          Got it
        </button>
      </div>
    </div>
  )
}
